// @flow
import { has } from "../../utils/helpers/objects";

import ResourceModel from "../base/ResourceModel";
import FilterCollection from "../filters/FilterCollection";
import ContentLinkModel from "./ContentLinkModel";
import ContentTypeModel from "./ContentTypeModel";
import Href from "../href/Href";

import SectionModel from "./SectionModel";

import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";
import type LinkModel from "../links/LinkModel";
import type { ModelOptions, ModularUIModel } from "../types";
import type { ModularUIResponse } from "../../modularui";

/**
 * Get content items recursively
 */
const getItems = (
  items: Array<Object>,
  entryDate: ISO_DATE | null,
  modelOptions?: ModelOptions,
): Array<ContentLinkModel> =>
  items.map((item) => {
    const link = new ContentLinkModel(
      {
        ...item,
        section: item._id,
      },
      entryDate,
      modelOptions,
    );

    if (item.items) {
      link.items = getItems(item.items, entryDate, modelOptions);
    }
    if (item.sections) {
      link.items = getItems(item.sections, entryDate, modelOptions);
    }

    return link;
  });

/**
 * Content detail model
 */
export default class ContentTOCModel extends ResourceModel {
  _contentType: ContentTypeModel | null = null;
  _filterCollection: FilterCollection;

  /**
   */
  get type(): string {
    return "ContentTOC";
  }

  /**
   */
  static get modelName(): string {
    return "ContentTOCModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return data.contributions?.resourcetype === "ContentTOC";
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const contentTypeLink = this.links.getLinkByKey("contenttype");

    if (contentTypeLink) {
      return [contentTypeLink];
    }

    return [];
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    this.contentType = models.find((model) => model.type === "ContentType");
  }

  /**
   * Get content label
   */
  get label(): string {
    return this.getData("label", "");
  }

  /**
   */
  get abbreviation(): string | null {
    return this.getData("abbreviation");
  }

  /**
   * Getting the self link of this list
   */
  get selfhref(): Href {
    const href = new Href(this.selflink.href);

    this.filterCollection.forEach((filter) => {
      filter.params.forEach((param) => {
        if (param.value) {
          href.setParameter(param.name, param.value);
        } else {
          href.removeParameter(param.name);
        }
      });
    });

    return href;
  }

  /**
   * Get related concepts link
   */
  get relatedConceptsHref(): Href | null {
    const relatedConceptsLink = this.links.getLinkByKey("relatedConcepts");

    if (relatedConceptsLink) {
      const { href } = relatedConceptsLink;
      href.addParameter("entryDate", this.entryDate);
      return href;
    }

    return null;
  }

  /**
   */
  get selfContentLink(): ContentLinkModel {
    return new ContentLinkModel(this.data, this.entryDate, this.modelOptions);
  }

  /**
   * Get sub items of toc
   */
  get items(): Array<ContentLinkModel> {
    if (this.data.items) {
      return getItems(this.data.items, this.entryDate, this.modelOptions);
    }

    if (this.data.sections) {
      return getItems(this.data.sections, this.entryDate, this.modelOptions);
    }

    return [];
  }

  /**
   */
  get sections(): Array<SectionModel> {
    if (this.data.sections) {
      return this.data.sections.map(
        (section) =>
          new SectionModel(section, this.entryDate, this.modelOptions),
      );
    }
    return [];
  }

  /**
   * get categories of content
   */
  get categories(): Array<ContentLinkModel> {
    if (this.data.categories) {
      return this.data.categories.map(
        (category) => new ContentLinkModel(category, null, this.modelOptions),
      );
    }

    return [];
  }

  /**
   * Retrieve content type model
   */
  get contentType(): ContentTypeModel | null {
    return this._contentType;
  }

  /**
   */
  set contentType(contentType: ?ModularUIModel) {
    this._contentType =
      contentType instanceof ContentTypeModel ? contentType : null;
  }

  /**
   * Retrieve available filters on concept toc
   */
  get filterCollection(): FilterCollection {
    if (!this._filterCollection) {
      this._filterCollection = new FilterCollection(
        this.data.filter,
        {
          filter: this.contributions.filter.filter((filter) => {
            const [key] = Object.keys(filter);
            return has(this.data?.filter, key);
          }),
        },
        this.modelOptions,
      );
    }

    return this._filterCollection;
  }

  /**
   * Retrieve modelcatalog.js of content toc
   */
  get entryDate(): ISO_DATE | null {
    const timeversionFilter = this.filterCollection.getFilterByAttributeKey(
      TIMEVERSION_FILTER_NAME,
    );

    const date = timeversionFilter?.value;
    if (typeof date === "string") {
      return date;
    }
    return null;
  }

  /**
   */
  get isCompleteSource(): boolean {
    const completeFilter =
      this.filterCollection.getFilterByAttributeKey("complete");
    return completeFilter?.value === "true";
  }
}
